<template>
    <div class="main page">
        <a-layout>
            <page-header />
            <a-layout-content class="info">
                <a-breadcrumb>
                    <a-breadcrumb-item
                        v-if="showBackButton"
                    >
                        <back-button @click="goBack" />
                    </a-breadcrumb-item>
                    <a-breadcrumb-item href="">
                        <home-outlined
                            @click="$router.push({name: 'main'})"
                        />
                    </a-breadcrumb-item>
                    <a-breadcrumb-item>
                        {{ patientName }}
                    </a-breadcrumb-item>
                </a-breadcrumb>
                <div class="main-info">
                    <a-tabs v-model:activeKey="activeTabKey">
                        <a-tab-pane
                            key="1"
                        >
                            <template #tab>
                                <span data-test-id="patient-main-tab">
                                    Основная
                                </span>
                            </template>
                            <main-info
                                ref="mainInfo"
                                :patient-id="patientId"
                                :patient-is-vip="patientIsVip"
                                class="card-info-wrapper"
                                @patient-info="patientName = $event.name"
                            />
                        </a-tab-pane>
                        <a-tab-pane
                            key="5"
                        >
                            <template #tab>
                                <span data-test-id="patient-anamnesis-tab">
                                    Анамнезы
                                </span>
                            </template>
                            <anamnesis-wrapper
                                v-if="patientId"
                                :patient-id="patientId"
                                @anamnesis-update="onAnamnesisUpdate"
                            />
                        </a-tab-pane>
                        <a-tab-pane
                            key="2"
                            force-render
                        >
                            <template #tab>
                                <span data-test-id="patient-treatment-history-tab">
                                    История лечения
                                </span>
                            </template>
                            <h2 :style="{marginLeft: '20px', marginBottom: '20px'}">
                                История лечения
                            </h2>

                            <div class="events-timeline">
                                <treatment-history
                                    :patient-id="patientId"
                                    :back-navigation-context="patientCardBackContext"
                                    :restore-state="treatmentHistoryRestore"
                                    :source-page-name="PAGE_NAMES.PATIENT_CARD"
                                    class="card-info-wrapper"
                                    @restored="treatmentHistoryRestore = null"
                                />
                            </div>
                        </a-tab-pane>
                        <a-tab-pane
                            v-if="userHasSurveysAccess"
                            key="3"
                        >
                            <template #tab>
                                <span data-test-id="patient-surveys-tab">
                                    Исследования
                                </span>
                            </template>
                            <surveys
                                :patient-id="patientId"
                                :back-navigation-context="patientCardBackContext"
                                :restore-state="surveysRestore"
                                :source-page-name="PAGE_NAMES.PATIENT_CARD"
                                class="card-info-wrapper"
                                @restored="surveysRestore = null"
                            />
                        </a-tab-pane>
                        <a-tab-pane
                            key="4"
                        >
                            <template #tab>
                                <span data-test-id="patient-polls-tab">
                                    Опросы
                                </span>
                            </template>
                            <polls
                                :patient-id="patientId"
                                :back-navigation-context="patientCardBackContext"
                                :restore-state="pollsRestore"
                                :source-page-name="PAGE_NAMES.PATIENT_CARD"
                                class="card-info-wrapper"
                                @restored="pollsRestore = null"
                            />
                        </a-tab-pane>
                        <a-tab-pane
                            key="6"
                        >
                            <template #tab>
                                <span data-test-id="patient-medication-calendar-tab">
                                    Мониторинг МикроРНК
                                </span>
                            </template>
                            <patient-medication-calendar
                                :patient-id="patientId"
                            />
                        </a-tab-pane>
                    </a-tabs>
                </div>
            </a-layout-content>
        </a-layout>
    </div>
</template>

<script>
import HomeOutlined from '@ant-design/icons-vue/HomeOutlined';
import { defineComponent } from 'vue';
import PatientMedicationCalendar
    from 'vue-web-components/src/components/patient-medication-calendar/PatientMedicationCalendar.vue';
import titleMixin from '@/mixins/titleMixin';
import BackButton from '@/components/BackButton.vue';
import PageHeader from '@/components/PageHeader.vue';
import {
    PAGE_NAMES,
    consumePendingRestoreContext,
} from '@/composables/useBackState';
import useBackButton from '@/composables/useBackButton';
import { restoreBackContext } from '@/utils/backContextRestore';
import MainInfo from '@/components/patientcard/MainInfo.vue';
import Surveys from '@/components/patientcard/Surveys.vue';
import Polls from '@/components/patientcard/Polls.vue';
import TreatmentHistory from '@/components/patientcard/TreatmentHistory.vue';
import AnamnesisWrapper from '@/components/patientcard/AnamnesisWrapper.vue';
import useUserRights from '@/utils/userRights';
import { showErrorNotification } from '@/utils';

export default defineComponent({
    components: {
        AnamnesisWrapper,
        BackButton,
        HomeOutlined,
        MainInfo,
        PageHeader,
        PatientMedicationCalendar,
        Polls,
        Surveys,
        TreatmentHistory,
    },
    mixins: [titleMixin],

    props: {
        patientId: {
            type: Number,
            default: null,
        },
    },

    setup() {
        const { userRightsMap } = useUserRights();
        const { goBack, showBackButton } = useBackButton();

        return {
            userRightsMap,
            goBack,
            showBackButton,
        };
    },

    data() {
        return {
            patientName: null,
            patientIsVip: null,
            activeTabKey: '1',
            treatmentHistoryRestore: null,
            surveysRestore: null,
            pollsRestore: null,
            PAGE_NAMES,
        };
    },

    computed: {
        patientCardBackContext() {
            return {
                patientId: this.patientId,
                activeTabKey: this.activeTabKey,
            };
        },

        userHasSurveysAccess() {
            return this.patientIsVip === false
                || (this.userRightsMap && this.userRightsMap.VIP_PATIENT_SURVEYS.r);
        },
    },

    watch: {
        patientId: {
            immediate: true,
            handler(val) {
                this.patientIsVip = null;
                if (!val) {
                    return;
                }

                this.$http.get(`patient/info/${val}`).then(
                    (response) => {
                        this.patientIsVip = !!response.data.isVip;
                    },
                    () => showErrorNotification('Ошибка загрузки данных пациента.'),
                );
            },
        },

        patientName(val) {
            if (val) {
                this.setPageTitle(val);
            }
        },
    },
    mounted() {
        const pending = consumePendingRestoreContext();

        restoreBackContext(pending, {
            [PAGE_NAMES.PATIENT_CARD]: (pageData) => this.restorePatientCardContext(pageData),
        });
    },

    methods: {
        restorePatientCardContext(pageData) {
            if (pageData.patientId && Number(pageData.patientId) !== this.patientId) {
                return;
            }

            if (typeof pageData.activeTabKey === 'string') {
                this.activeTabKey = pageData.activeTabKey;
            }

            if (pageData.treatmentHistory) {
                this.treatmentHistoryRestore = pageData.treatmentHistory;
            }

            if (pageData.surveys) {
                this.surveysRestore = pageData.surveys;
            }

            if (pageData.polls) {
                this.pollsRestore = pageData.polls;
            }
        },

        onAnamnesisUpdate() {
            if (Object.prototype.hasOwnProperty.call(this.$refs, 'mainInfo')) {
                this.$refs.mainInfo.getPatientHealthPassport(this.patientId);
            }
        },
    },
});
</script>

<style lang="scss">
.main.page {
    .ant-layout-content {
        .ant-tabs-nav-scroll {
            text-align: left;
            padding-left: 20px;
        }
    }
}
</style>
